import type { Country } from './common';

/**
 * Implements:
 * https://en.wikipedia.org/wiki/National_identification_number#Sweden
 */
export class SE implements Country {
	private static readonly VALIDATION_REGEX = /^([0-9]{6}|[0-9]{8})[-+]?[0-9A-Z]{4}$/i;

	/** `YYMMDDXXXX` */
	public readonly minLength = 10;

	public validate(input: string): boolean {
		if (input.length < 10 || input.length > 13 || !SE.VALIDATION_REGEX.test(input)) {
			return false;
		}

		const normalized = this.normalize(input);
		// Month cant be over 12
		if (parseInt(normalized.substring(4, 6)) > 12) return false;

		// Apply checksum if there are no letters (expected behavior with letters is unknown)
		if (normalized.match(/[A-Z]/gi) === null && !this.luhnCheck(normalized.substring(2))) {
			return false;
		}

		return true;
	}

	public normalize(input: string): string {
		// Normalize to YYYYMMDDXXXZ
		let normalized = '';
		const centuryOffset = input[6] === '+' ? -1 : 0;
		switch (input.length) {
			case 11: // YYMMDD?XXXZ
				normalized =
					Math.floor((new Date().getFullYear() - parseInt(input.substring(0, 2))) / 100) +
					centuryOffset +
					input.substring(0, 6) +
					input.substring(7);
				break;

			case 10: // YYMMDDXXXZ (century guessing; by the year you were born, not the birth date)
				normalized =
					Math.floor((new Date().getFullYear() - parseInt(input.substring(0, 2))) / 100) + input.substring(0, 6) + input.substring(6);
				break;

			case 12: // YYYYMMDDXXXZ
				normalized = input;
				break;

			case 13: // YYYYMMDD?XXXZ
				normalized = input.substring(0, 8) + input.substring(9);
				break;
		}

		return normalized;
	}

	public getDateOfBirth(input: string): Date | null {
		if (!this.validate(input)) return null;
		const dateOfBirth = this.normalize(input).slice(0, 8);

		const year = +dateOfBirth.slice(0, 4);
		const month = +dateOfBirth.slice(4, 6);
		let day = +dateOfBirth.slice(6, 8);

		// If it's a samordningsnummer, then correct the birthday
		if (day > 60) {
			day -= 60;
		}

		return new Date(year, month - 1, day, 0, 0, 0, 0);
	}

	/**
	 * Luhn algorithm in JavaScript: validate credit card number supplied as string of numbers
	 * @author ShirtlessKirk. Copyright (c) 2012.
	 * @license WTFPL (http://www.wtfpl.net/txt/copying)
	 */
	private luhnCheck(input: string): boolean {
		const arr = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
		let len = input.length;
		let bit = 1;
		let sum = 0;
		let val: number;

		while (len) {
			val = parseInt(input.charAt(--len), 10);
			// eslint-disable-next-line no-cond-assign
			sum += (bit ^= 1) ? arr[val] : val;
		}

		return !!sum && sum % 10 === 0;
	}
}
